export enum AccountState {
  // See: https://taktak.atlassian.net/wiki/spaces/ENG/pages/1779499247/Account+state+machine+and+pools
  /** ********** ROOT OU *********** */
  AccountProv = 'Account-Prov',
  /** ********** Account Provisioning OU *********** */
  AccountReady = 'Account-Ready',
  /** ********** Maestro Provisioning OU *********** */
  TerraformProv = 'Terraform-Prov',
  PreProv = 'Pre-Prov',
  /** Pre-prov triggered via API */
  TerraformPreProvSuccess = 'Terraform-Pre-Prov-Success',
  TerraformPreProvFailed = 'Terraform-Pre-Prov-Failed',
  /**
   * @deprecated shouldn't be used anymore, will be deleted soon
   */
  PostProv = 'Post-Prov',
  /** ********** Assignable OU *********** */
  TenantReady = 'Tenant-Ready',
  /** ********** Assigned OU *********** */
  TenantAssigned = 'Tenant-Assigned',
  TenantUnassigned = 'Tenant-Unassigned',
  TenantUpdated = 'Tenant-Updated', // An organization will never be in this state, it's just used for messaging
  TenantFailedUpdate = 'Tenant-Failed-Update', // An organization will never be in this state, it's just used for messaging
  // ...More to be added when applicable
  /** ********** Cleanup OU *********** */
  CleanupRequested = 'Cleanup-Requested',
  CleanupStarted = 'Cleanup-Started',
  CleanupCompleted = 'Cleanup-Completed',
  CleanupFailed = 'Cleanup-Failed',
  /** ********** Available OU *********** */
  AccountAvailable = 'Account-Available',
  /** ********** Failed OU *********** */
  TenantFailed = 'Tenant-Failed',
  TenantFailedDLQ = 'Tenant-FailedDLQ', // Failed to DLQ. An organization will never be in this state, it's just used for metrics
  TenantBanished = 'Tenant-Banished', // organization record surgically updated due to inconsistent state during migration from AWS tags
  /** ********** Workspaces *********** */
  WorkspaceRequested = 'Workspace-Requested',
  WorkspaceProvisioning = 'Workspace-Provisioning',
  WorkspaceProvisioningFailed = 'Workspace-Provisioning-Failed',
  WorkspaceProvisioned = 'Workspace-Provisioned',
  WorkspaceActive = 'Workspace-Active',
  WorkspaceAsleep = 'Workspace-Asleep',
  WorkspaceAsleepRequested = 'Workspace-Asleep-Requested',
  WorkspaceWakeRequested = 'Workspace-Wake-Requested',
  WorkspaceUpdated = 'Workspace-Updated',
  WorkspaceFailedUpdate = 'Workspace-Failed-Update',
  WorkspaceCleanupRequested = 'Workspace-Cleanup-Requested',
  WorkspaceCleanupStarted = 'Workspace-Cleanup-Started',
  WorkspaceCleanupTerraformCompleted = 'Workspace-Cleanup-Terraform-Completed',
  WorkspaceCleanupTerraformFailed = 'Workspace-Cleanup-Terraform-Failed',
  WorkspaceCleanupCompleted = 'Workspace-Cleanup-Completed',
  WorkspaceCleanupFailed = 'Workspace-Cleanup-Failed',
}

export type WorkspaceState =
  | AccountState.WorkspaceRequested |
  AccountState.WorkspaceProvisioning |
  AccountState.WorkspaceProvisioningFailed |
  AccountState.WorkspaceProvisioned |
  AccountState.WorkspaceActive |
  AccountState.WorkspaceAsleep |
  AccountState.WorkspaceAsleepRequested |
  AccountState.WorkspaceWakeRequested |
  AccountState.WorkspaceCleanupRequested |
  AccountState.WorkspaceCleanupStarted |
  AccountState.WorkspaceCleanupTerraformCompleted |
  AccountState.WorkspaceCleanupTerraformFailed |
  AccountState.WorkspaceCleanupCompleted |
  AccountState.WorkspaceCleanupFailed;

export const AllAccountStates = Object.values(AccountState);

const accountStateValues = new Set<string>(AllAccountStates);

export const isAccountState = (value: unknown): value is AccountState =>
  typeof value === 'string' && accountStateValues.has(value);
export const AllWorkspaceStates = AllAccountStates.filter((i) => i.indexOf('Workspace-') > -1);
export const AllActiveWorkspaceStates = AllWorkspaceStates.filter((i) => i.indexOf('Cleanup') < 0);

export const cleanableStates: AccountState[] = [
  AccountState.AccountProv, // Failure to launch
  AccountState.PreProv,
  AccountState.PostProv,
  // TODO This state should be removed and fix testing accordingly
  // We don't want this state be cleanable
  AccountState.TenantReady, // Assignable
  AccountState.TenantUnassigned, // Not assigned anymore
  AccountState.CleanupFailed,
  AccountState.AccountAvailable,
  AccountState.CleanupRequested,
  AccountState.TenantFailed,
  AccountState.TenantFailedDLQ,
];

export const restorableStates: AccountState[] = [
  AccountState.TenantUnassigned, // Not assigned anymore
  AccountState.CleanupFailed,
  AccountState.CleanupCompleted,
];

export const workspaceFailedStates: readonly AccountState[] = [
  AccountState.WorkspaceProvisioningFailed,
  AccountState.WorkspaceCleanupTerraformFailed,
  AccountState.WorkspaceCleanupFailed,
];

// Narrower than workspaceFailedStates: only states reached AFTER a cleanup attempt was made and failed.
// WorkspaceProvisioningFailed is deliberately excluded -- a workspace that never got a cleanup attempt
// is still cleanable and must not count toward the managed-cleanup all-failed tripwire (INFRA-11349).
export const workspaceTerminalCleanupFailedStates: readonly AccountState[] = [
  AccountState.WorkspaceCleanupTerraformFailed,
  AccountState.WorkspaceCleanupFailed,
];

export const workspaceDeletingStates: readonly AccountState[] = [
  AccountState.WorkspaceCleanupRequested,
  AccountState.WorkspaceCleanupStarted,
  AccountState.WorkspaceCleanupTerraformCompleted,
  AccountState.WorkspaceCleanupCompleted,
];

export const workspaceHibernationStates: readonly AccountState[] = [
  AccountState.WorkspaceAsleep,
  AccountState.WorkspaceAsleepRequested,
  AccountState.WorkspaceWakeRequested,
];

// States that we restrict the UI to be able to delete in: active/failed
// This prevents delete requests causing terraform collisions when transitioning states
// EXCLUDE WorkspaceRequested to prevent cleanup during initial provisioning setup
export const workspaceDeletableStates = [
  ...AllActiveWorkspaceStates.filter((state) => state !== AccountState.WorkspaceRequested),
  ...workspaceFailedStates,
];
